import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Mail, Send, SkipForward } from 'lucide-react';
import {
  useBulkActivationNudge, useNudgePreview,
  type AdminUserRow, type BulkNudgeResult, type BulkNudgeTarget,
} from '@/hooks/useAdminUsers';
import { STEP_KEYS, STEP_LABELS } from './LeadFilters';

const DAY = 86400000;

const SKIP_LABELS: Record<string, string> = {
  unconfirmed: 'e-mail não confirmado',
  no_step: 'sem etapa travada (funil completo)',
  recent: 'já recebeu estímulo nas últimas 72h',
};

const ERROR_LABELS: Record<string, string> = {
  email_not_confirmed: 'e-mail não confirmado',
  email_suppressed: 'e-mail suprimido (bounce/reclamação)',
  recently_contacted: 'contato recente (72h)',
  invalid_funnel_step: 'etapa inválida',
  send_failed: 'falha no envio',
  unknown: 'erro inesperado',
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  leads: AdminUserRow[];
}

export function BulkNudgeDialog({ open, onOpenChange, leads }: Props) {
  const [mode, setMode] = useState<'auto' | 'uniform'>('auto');
  const [uniformStep, setUniformStep] = useState('patient_created');
  const [message, setMessage] = useState('');
  const [force, setForce] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<BulkNudgeResult[] | null>(null);

  const bulk = useBulkActivationNudge();

  useEffect(() => {
    if (open) {
      setMode('auto');
      setMessage('');
      setForce(false);
      setProgress(null);
      setResults(null);
    }
  }, [open]);

  const { targets, skipped } = useMemo(() => {
    const targets: BulkNudgeTarget[] = [];
    const skipped: Array<{ row: AdminUserRow; reason: string }> = [];
    for (const r of leads) {
      if (r.email_confirmed === false) {
        skipped.push({ row: r, reason: 'unconfirmed' });
        continue;
      }
      const step = mode === 'uniform' ? uniformStep : r.blocked_step;
      if (!step) {
        skipped.push({ row: r, reason: 'no_step' });
        continue;
      }
      const recent = r.last_outreach_at && Date.now() - new Date(r.last_outreach_at).getTime() < 3 * DAY;
      if (recent && !force) {
        skipped.push({ row: r, reason: 'recent' });
        continue;
      }
      targets.push({
        userId: r.user_id,
        email: r.email,
        name: r.full_name,
        funnelStep: step,
        daysStuck: r.days_stuck ?? null,
      });
    }
    return { targets, skipped };
  }, [leads, mode, uniformStep, force]);

  const stepDistribution = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of targets) map.set(t.funnelStep, (map.get(t.funnelStep) ?? 0) + 1);
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [targets]);

  const previewTarget = targets[0];
  const preview = useNudgePreview({
    userId: previewTarget?.userId ?? '',
    funnelStep: previewTarget?.funnelStep ?? 'patient_created',
    customMessage: message.trim() || null,
    daysStuck: previewTarget?.daysStuck ?? null,
    enabled: open && !!previewTarget && !results,
  });

  const handleSend = () => {
    setResults(null);
    setProgress({ done: 0, total: targets.length });
    bulk.mutate(
      {
        targets,
        customMessage: message.trim() || null,
        force,
        onProgress: (done, total) => setProgress({ done, total }),
      },
      {
        onSuccess: (res) => {
          setResults(res);
          const ok = res.filter((r) => r.ok).length;
          const fail = res.length - ok;
          if (fail === 0) toast.success(`${ok} e-mail(s) de estímulo enviado(s)`);
          else toast.warning(`${ok} enviado(s), ${fail} não enviado(s)`);
        },
        onError: () => toast.error('Falha ao processar o envio em lote.'),
      },
    );
  };

  const d = preview.data?.template_data;
  const sending = bulk.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sending) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            Enviar estímulo para {leads.length} lead(s)
          </DialogTitle>
          <DialogDescription>
            Cada lead recebe o próprio e-mail, com link de acompanhamento individual.
          </DialogDescription>
        </DialogHeader>

        {results ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-success/15 text-success hover:bg-success/15">
                {results.filter((r) => r.ok).length} enviados
              </Badge>
              <Badge variant="outline">{skipped.length} pulados</Badge>
              {results.some((r) => !r.ok) && (
                <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15">
                  {results.filter((r) => !r.ok).length} com erro
                </Badge>
              )}
            </div>
            <div className="rounded-lg border border-border divide-y divide-border max-h-64 overflow-y-auto">
              {results.map((r) => (
                <div key={r.userId} className="flex items-center gap-2 p-2 text-sm">
                  {r.ok
                    ? <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    : <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />}
                  <span className="truncate flex-1">{r.name || r.email}</span>
                  {!r.ok && (
                    <span className="text-xs text-muted-foreground">
                      {ERROR_LABELS[r.errorKey ?? 'unknown'] ?? r.errorKey}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Conteúdo do e-mail</Label>
              <RadioGroup value={mode} onValueChange={(v) => setMode(v as 'auto' | 'uniform')} className="gap-2">
                <div className="flex items-start gap-2 rounded-md border border-border p-3">
                  <RadioGroupItem value="auto" id="mode-auto" className="mt-0.5" />
                  <Label htmlFor="mode-auto" className="font-normal cursor-pointer">
                    <span className="font-medium block">Automático por etapa (recomendado)</span>
                    <span className="text-xs text-muted-foreground">
                      Cada lead recebe o e-mail da etapa em que ele está parado.
                    </span>
                  </Label>
                </div>
                <div className="flex items-start gap-2 rounded-md border border-border p-3">
                  <RadioGroupItem value="uniform" id="mode-uniform" className="mt-0.5" />
                  <Label htmlFor="mode-uniform" className="font-normal cursor-pointer">
                    <span className="font-medium block">Etapa única para todos</span>
                    <span className="text-xs text-muted-foreground">
                      Todos recebem o conteúdo da etapa escolhida.
                    </span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {mode === 'uniform' && (
              <div className="space-y-1.5">
                <Label>Etapa</Label>
                <Select value={uniformStep} onValueChange={setUniformStep}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STEP_KEYS.map((k) => (
                      <SelectItem key={k} value={k}>{STEP_LABELS[k]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Mensagem pessoal (opcional, igual para todos)</Label>
              <Textarea
                value={message}
                maxLength={600}
                rows={3}
                placeholder="Ex.: Qualquer dúvida me chama, posso montar o primeiro plano junto com você."
                onChange={(e) => setMessage(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{message.length}/600</p>
            </div>

            <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Distribuição do envio
              </p>
              {stepDistribution.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum lead elegível na seleção atual.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {stepDistribution.map(([k, n]) => (
                    <Badge key={k} variant="outline">{STEP_LABELS[k] ?? k}: {n}</Badge>
                  ))}
                </div>
              )}
            </div>

            {skipped.length > 0 && (
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <SkipForward className="h-3.5 w-3.5" />
                  {skipped.length} lead(s) serão pulados
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {skipped.map((s) => (
                    <p key={s.row.user_id} className="text-xs text-muted-foreground truncate">
                      {s.row.full_name || s.row.email} — {SKIP_LABELS[s.reason]}
                    </p>
                  ))}
                </div>
                {skipped.some((s) => s.reason === 'recent') && !force && (
                  <Button size="sm" variant="outline" onClick={() => setForce(true)}>
                    <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                    Forçar envio para quem recebeu nas últimas 72h
                  </Button>
                )}
              </div>
            )}

            <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Pré-visualização {previewTarget ? `(${previewTarget.name || previewTarget.email})` : ''}
              </p>
              {!previewTarget ? (
                <p className="text-sm text-muted-foreground">Selecione leads elegíveis para pré-visualizar.</p>
              ) : preview.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ) : d ? (
                <div className="space-y-2">
                  <p className="font-semibold text-sm">{d.stepHeadline}</p>
                  <p className="text-sm">{d.stepDescription}</p>
                  <p className="text-sm text-primary">{d.benefit}</p>
                  {d.adminMessage && (
                    <p className="text-sm border border-dashed border-border rounded-md p-2 whitespace-pre-wrap">
                      {d.adminMessage}
                    </p>
                  )}
                  <Badge className="bg-primary text-primary-foreground">{d.ctaLabel}</Badge>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Não foi possível gerar a pré-visualização.</p>
              )}
            </div>

            {progress && sending && (
              <div className="space-y-1">
                <Progress value={(progress.done / Math.max(progress.total, 1)) * 100} />
                <p className="text-xs text-muted-foreground">
                  Enviando {progress.done} de {progress.total}...
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            {results ? 'Fechar' : 'Cancelar'}
          </Button>
          {!results && (
            <Button onClick={handleSend} disabled={sending || targets.length === 0}>
              <Send className="h-4 w-4 mr-1" />
              {sending ? 'Enviando...' : `Enviar para ${targets.length} lead(s)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
